import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FileCheck } from "lucide-react";

interface MarkAsFiledDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
  clientEmail: string;
  onComplete?: () => void;
}

export function MarkAsFiledDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  clientEmail,
  onComplete,
}: MarkAsFiledDialogProps) {
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleMarkAsFiled = async () => {
    if (!clientId) return;

    setIsSubmitting(true);
    try {
      // Update client record with filing date
      const { error: updateError } = await supabase
        .from("clients")
        .update({
          taxes_filed_date: new Date().toISOString().split('T')[0],
          filing_confirmation_sent: false, // Will be set to true after email sends
        })
        .eq("id", clientId);

      if (updateError) throw updateError;

      // Get the taxes_filed email template
      const { data: template, error: templateError } = await supabase
        .from("email_templates")
        .select("id")
        .eq("template_name", "taxes_filed")
        .eq("is_active", true)
        .single();

      if (templateError || !template) {
        throw new Error("taxes_filed email template not found");
      }

      // Send the email
      const { error: emailError } = await supabase.functions.invoke("send-email", {
        body: {
          template_id: template.id,
          client_id: clientId,
          recipient_email: clientEmail,
          variables: {
            client_name: clientName,
            filing_date: new Date().toLocaleDateString('en-US', { 
              month: 'long', 
              day: 'numeric', 
              year: 'numeric' 
            }),
            notes: notes || 'Your tax return has been filed successfully.',
          },
        },
      });

      if (emailError) throw emailError;

      // Mark email as sent
      await supabase
        .from("clients")
        .update({ filing_confirmation_sent: true })
        .eq("id", clientId);

      // Log activity
      await supabase.from("activity_log").insert({
        client_id: clientId,
        user_id: (await supabase.auth.getUser()).data.user?.id,
        action: "taxes_filed",
        details: {
          notes,
          email_sent: true,
        },
      });

      toast.success("Tax filing marked complete and email sent!");
      onOpenChange(false);
      setNotes("");
      onComplete?.();
    } catch (error: any) {
      console.error("Error marking taxes as filed:", error);
      toast.error(error.message || "Failed to send filing confirmation");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck className="h-5 w-5" />
            Mark Taxes as Filed
          </DialogTitle>
          <DialogDescription>
            This will mark {clientName}'s taxes as filed and send them a confirmation email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any special instructions or notes for the client..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
            />
          </div>

          <div className="text-sm text-muted-foreground">
            Email will be sent to: <strong>{clientEmail}</strong>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMarkAsFiled}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sending..." : "Mark Filed & Send Email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}